import { useEffect, useState } from "react";
import "./Homepage.css";

const SHEET_ID = "17ieWgbfJ1fSuECFPdmS4NugXS5F65zlXy4w9_GnuKaY";
const BASE_URL = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:json`;

const VENDORS_URL = `${BASE_URL}&sheet=Vendors`;
const TIERS_URL = `${BASE_URL}&sheet=Vendor_Tiers`;
const LEADS_URL = `${BASE_URL}&sheet=Leads`;
const BOOKINGS_URL = `${BASE_URL}&sheet=Bookings`;

/* ---------- Utils ---------- */

function formatDurationFromDays(totalDays) {
  if (!Number.isFinite(totalDays) || totalDays < 0) return "0 days";

  if (totalDays < 30) return `${totalDays} day${totalDays !== 1 ? "s" : ""}`;

  if (totalDays < 365) {
    const months = Math.floor(totalDays / 30);
    const days = totalDays % 30;
    return days
      ? `${months} months, ${days} days`
      : `${months} months`;
  }

  const years = Math.floor(totalDays / 365);
  const rem = totalDays % 365;
  const months = Math.floor(rem / 30);
  const days = rem % 30;

  return [
    `${years} years`,
    months && `${months} months`,
    days && `${days} days`,
  ]
    .filter(Boolean)
    .join(", ");
}

function parseTieUpDate(value) {
  if (value instanceof Date) return value;

  if (typeof value === "string" && value.startsWith("Date(")) {
    const [y, m, d] = value
      .replace("Date(", "")
      .replace(")", "")
      .split(",")
      .map(Number);
    return new Date(y, m, d);
  }

  if (typeof value === "string") {
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) return parsed;
  }

  if (typeof value === "number") {
    return new Date((value - 25569) * 86400 * 1000);
  }

  return null;
}

function parseSheet(text) {
  const json = JSON.parse(
    text.substring(text.indexOf("{"), text.lastIndexOf("}") + 1)
  );

  const cols = json.table.cols.map((c) => c.label);

  return json.table.rows.map((row) => {
    const obj = {};
    row.c.forEach((cell, i) => {
      obj[cols[i]] = cell?.v ?? "";
    });
    return obj;
  });
}

/* ---------- Component ---------- */

function Homepage({ user, onLogout }) {
  const [vendor, setVendor] = useState(null);
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadDashboard() {
      try {
        const [vendorsRes, tiersRes, leadsRes, bookingsRes] =
          await Promise.all([
            fetch(VENDORS_URL),
            fetch(TIERS_URL),
            fetch(LEADS_URL),
            fetch(BOOKINGS_URL),
          ]);

        const vendors = parseSheet(await vendorsRes.text());
        const tiers = parseSheet(await tiersRes.text());
        const allLeads = parseSheet(await leadsRes.text());
        const allBookings = parseSheet(await bookingsRes.text());

        const currentVendor = vendors.find(
          (v) => v.vendor_id === user.vendor_id
        );
        if (!currentVendor) return;

        const tier = tiers.find((t) => t.tier === currentVendor.tier);

        setVendor({
          ...currentVendor,
          commission_percent: tier?.commission_percent ?? 0,
        });

        const vendorLeads = allLeads.filter(
          (l) => l.vendor_id === currentVendor.vendor_id
        );

        const leadIds = vendorLeads.map((l) => l.lead_id);
        const vendorBookings = allBookings.filter((b) =>
          leadIds.includes(b.lead_id)
        );

        setLeads(vendorLeads);
        setBookings(vendorBookings);
      } catch (err) {
        console.error("Dashboard load error", err);
      } finally {
        setLoading(false);
      }
    }

    if (user?.vendor_id) loadDashboard();
  }, [user?.vendor_id]);

  if (loading) return <div className="page-center">Loading dashboard…</div>;
  if (!vendor) return <div className="page-center">No vendor record found.</div>;

  const tieUpDate = parseTieUpDate(vendor.tie_up_date);
  const tieUpDays = tieUpDate
    ? Math.floor((Date.now() - tieUpDate.getTime()) / 86400000)
    : 0;

  const totalLeads = leads.length;
  const convertedLeads = bookings.length;

  const conversionRate =
    totalLeads > 0
      ? ((convertedLeads / totalLeads) * 100).toFixed(1)
      : "0.0";

  return (
    <div className="dashboard">
      <header className="topbar">
        <div className="taf">
          <img src="/logo.png" alt="Logo" />
          <h1>The Amaltaas Fort</h1>
        </div>
        <div className="brand">
          <h1>{vendor.vendor_name}</h1>
          <span className="subtle">
            Tier {vendor.tier} · {vendor.commission_percent}% commission
          </span>
        </div>

        <button className="logout-link" onClick={onLogout}>
          Sign out →
        </button>
      </header>

      <section className="metrics">
        <div className="metric-card">
          <span>TOTAL LEADS</span>
          <strong>{totalLeads}</strong>
        </div>
        <div className="metric-card">
          <span>CONVERTED</span>
          <strong>{convertedLeads}</strong>
        </div>
        <div className="metric-card">
          <span>CONVERSION RATE</span>
          <strong>{conversionRate}%</strong>
        </div>
        <div className="metric-card">
          <span>DAYS SINCE TIE-UP</span>
          <strong>{formatDurationFromDays(tieUpDays)}</strong>
        </div>
      </section>

      <section className="info-grid">
        <div className="panel">
          <h3>Confirmed Bookings</h3>
          {bookings.length === 0 ? (
            <p className="muted">No confirmed bookings yet</p>
          ) : (
            <ul className="list">
              {bookings.map((b, i) => (
                <li key={i}>{b.booking_date}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="panel">
          <h3>Vendor Details</h3>
          <ul className="details">
            <li><strong>Email:</strong> {vendor.email}</li>
            <li><strong>Tier:</strong> {vendor.tier}</li>
            <li><strong>Commission:</strong> {vendor.commission_percent}%</li>
          </ul>
        </div>
      </section>

      <section className="panel">
        <h3>Leads</h3>
        <div className="table-wrap">
          <table className="leads-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Status</th>
                <th>Quote Given</th>
                <th>Booking Confirmed</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.lead_id}>
                  <td>{lead.company_name}</td>
                  <td>{lead.status}</td>
                  <td>{lead.quote_given}</td>
                  <td>{lead.booking_confirmed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default Homepage;
